import { UPGRADE_FUNNEL_BROWSER_EVENT } from '@shared/core/analytics'
import type { PaywallViewedProperties } from '@shared/core/analytics'
import { REMINDERS_PAUSED_REASONS } from '@shared/core/billing'
import classNames from 'classnames'
import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useCalendars } from 'src/modules/calendar/hooks/use-calendars'
import { FormattedText, Modal, Typography } from 'src/modules/core/components'
import { useDisplayTimezone } from 'src/modules/core/context'
import { useTrackUpgradeFunnel } from 'src/modules/payment/hooks/use-track-upgrade-funnel'
import { formatSubscriptionEndedOn } from 'src/modules/payment/utils/helpers'
import type {
  AtRiskAppointmentRowsProps,
  PausedRemindersModalBodyProps,
  PausedRemindersModalProps,
} from './paused-reminders-modal.interface'
import classes from './paused-reminders-modal.module.scss'
import {
  ENDED_REMINDERS_MODAL_PLANS_PATH,
  formatAtRiskAppointmentStart,
  PAUSED_REMINDERS_MODAL_PLANS_PATH,
  PAUSED_REMINDERS_MODAL_VIEW_PROPERTIES,
} from './paused-reminders-modal.domain'

export function PausedRemindersModal({
  open,
  onClose,
  profile,
  pausedReminders,
  reason,
  viewProperties = PAUSED_REMINDERS_MODAL_VIEW_PROPERTIES,
}: PausedRemindersModalProps) {
  usePaywallViewedOnOpen(open, viewProperties)

  if (!open) {
    return null
  }

  const { notSentCount, atRisk } = pausedReminders
  const hasRows = atRisk.rows.length > 0
  const plansPath =
    reason === REMINDERS_PAUSED_REASONS.subscriptionEnded
      ? ENDED_REMINDERS_MODAL_PLANS_PATH
      : PAUSED_REMINDERS_MODAL_PLANS_PATH

  return (
    <Modal
      variant="primary"
      closeOnBackdropClick={false}
      onClose={onClose}
      contentClassName={classes.content}
      title={
        <Typography variant="h3" className={classes.title}>
          {notSentCount > 0 ? (
            <FormattedText id="dashboard.paused.modal.title" values={{ count: notSentCount }} />
          ) : (
            <FormattedText id="dashboard.paused.modal.titleNeutral" />
          )}
        </Typography>
      }
    >
      <PausedRemindersModalBody profile={profile} reason={reason} hasRows={hasRows} />
      {hasRows && <AtRiskAppointmentRows rows={atRisk.rows} moreCount={atRisk.count - atRisk.rows.length} />}
      <FeaturePills />
      <div className={classes.actions}>
        <Link to={plansPath} className={classes.upgradeButton}>
          <FormattedText id="dashboard.paused.modal.cta" />
        </Link>
        <button type="button" className={classes.dismissButton} onClick={onClose}>
          <FormattedText id="dashboard.paused.modal.dismiss" />
        </button>
      </div>
    </Modal>
  )
}

function PausedRemindersModalBody({ profile, reason, hasRows }: PausedRemindersModalBodyProps) {
  if (reason === REMINDERS_PAUSED_REASONS.subscriptionEnded) {
    return (
      <p className={classes.body}>
        <FormattedText
          id={hasRows ? 'dashboard.paused.modal.bodyEnded' : 'dashboard.paused.modal.bodyEndedNoUpcoming'}
          values={{ date: formatSubscriptionEndedOn(profile) }}
        />
      </p>
    )
  }

  return (
    <p className={classes.body}>
      <FormattedText
        id={hasRows ? 'dashboard.paused.modal.body' : 'dashboard.paused.modal.bodyNoUpcoming'}
        values={{ limit: profile.effective_free_reminders_limit }}
      />
    </p>
  )
}

function usePaywallViewedOnOpen(open: boolean, viewProperties: PaywallViewedProperties) {
  const trackUpgradeFunnel = useTrackUpgradeFunnel()
  const hasTrackedRef = useRef(false)

  useEffect(() => {
    if (!open || hasTrackedRef.current) {
      return
    }

    hasTrackedRef.current = true
    trackUpgradeFunnel(UPGRADE_FUNNEL_BROWSER_EVENT.PAYWALL_VIEWED, viewProperties)
  }, [open, trackUpgradeFunnel, viewProperties])
}

function AtRiskAppointmentRows({ rows, moreCount }: AtRiskAppointmentRowsProps) {
  const { timezone } = useDisplayTimezone()
  const calendars = useCalendars()

  return (
    <div className={classes.rows} data-testid="paused-reminders-modal-rows">
      {rows.map((event) => (
        <div key={`${event.id}-${event.event_date}`} className={classes.row}>
          <span
            className={classes.calendarDot}
            style={{ backgroundColor: calendars.data?.find((calendar) => calendar.id === event.calendar)?.color }}
          />
          <span className={classes.rowTitle} title={event.title ?? undefined}>
            {event.title || <FormattedText id="reminder.noTitle" />}
          </span>
          <span className={classes.rowDate}>{formatAtRiskAppointmentStart(event, timezone)}</span>
          <span className={classes.noReminderBadge}>
            <FormattedText id="dashboard.paused.modal.noReminder" />
          </span>
        </div>
      ))}
      {moreCount > 0 && (
        <div className={classes.more}>
          <FormattedText id="dashboard.paused.modal.more" values={{ count: moreCount }} />
        </div>
      )}
    </div>
  )
}

function FeaturePills() {
  return (
    <div className={classes.pills}>
      <span className={classNames(classes.pill, classes.pillFree)}>
        <CheckIcon />
        <FormattedText id="dashboard.paused.modal.bookingFree" />
      </span>
      <span className={classNames(classes.pill, classes.pillFree)}>
        <CheckIcon />
        <FormattedText id="dashboard.paused.modal.calendarSyncFree" />
      </span>
      <span className={classNames(classes.pill, classes.pillPaused)}>
        <PauseIcon />
        <FormattedText id="dashboard.paused.modal.smsPaused" />
      </span>
    </div>
  )
}

function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="#55AFB0" strokeWidth="2.4" aria-hidden="true">
      <path d="M2.5 7.5l3 3 6-7" />
    </svg>
  )
}

function PauseIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 12 12" fill="#D4960B" aria-hidden="true">
      <rect x="2" y="1.5" width="3" height="9" rx=".8" />
      <rect x="7" y="1.5" width="3" height="9" rx=".8" />
    </svg>
  )
}
